import { useRef } from "react";

import { Timezone } from "@/constants/dateTime";
import { getStreamUrlDatasetStream } from "@/helpers/datasetAttributeHelper";
import {
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetDataRowV2,
  DatasetFilter,
  DatasetMetadata,
  DatasetMetadataStream,
  DatasetStreamDone,
  H3Configuration
} from "@/types/dataset";

import useEventSource from "./useEventSource";

import { useDataset } from "./map/useDataset";

export interface DatasetStreamComplete {
  datasetMetadata: DatasetMetadata;
  rawDataset: DatasetData;
  dataset: Dataset;
}

interface IUseDatasetStreamProps {
  datasetId?: string;
  enabled: boolean;
  filters?: DatasetFilter[];
  timezone?: Timezone;
  h3Configuration?: H3Configuration | null;
  onOpen?: () => void;
  onDatasetMetadata?: (data: DatasetMetadata) => void;
  onStreamComplete?: (data: DatasetStreamComplete) => void | Promise<void>;
  onError?: (data: unknown) => void;
}

type Error = { code: number; message: string };

export type DatasetStream = {
  dataset_detail: DatasetMetadata;
  "dataset_data:meta": DatasetMetadataStream;
  "dataset_data:chunk": DatasetChunkStream;
  "dataset_data:done": DatasetStreamDone;
  "stream:done": { dataset_id: string };
  "stream:error": Error;
  "stream:reset": Error;
  error: Error;
};

export function useDatasetStream({
  datasetId,
  filters,
  timezone,
  h3Configuration,
  enabled,
  onStreamComplete,
  onError,
  onOpen
}: IUseDatasetStreamProps) {
  const { generateDatasetData } = useDataset();

  const chunkBufferRef = useRef<DatasetDataRowV2[]>([]);
  const datasetMetadataRef = useRef<DatasetMetadata>();
  const datasetMetadataStreamRef = useRef<DatasetMetadataStream>();
  const datasetDataRef = useRef<{
    rawDataset: DatasetData;
    dataset: Dataset;
  }>();

  const handleDatasetDone = (data: DatasetStreamDone) => {
    const datasetChunk = {
      dataset_id: data.dataset_id,
      rows: chunkBufferRef.current,
      seq: data.final_seq
    };

    if (datasetMetadataStreamRef.current && datasetMetadataRef.current) {
      const { datasetData: rawDataset, newDataset: dataset } =
        generateDatasetData(
          datasetChunk,
          datasetMetadataStreamRef.current,
          true,
          h3Configuration
        );

      datasetDataRef.current = {
        rawDataset,
        dataset
      };
    }

    chunkBufferRef.current = [];
  };

  const handleStreamDone = () => {
    if (
      datasetMetadataRef.current &&
      datasetDataRef.current &&
      datasetMetadataStreamRef.current
    ) {
      onStreamComplete?.({
        datasetMetadata: datasetMetadataRef.current,
        rawDataset: datasetDataRef?.current?.rawDataset,
        dataset: datasetDataRef?.current?.dataset
      });
    }
  };

  useEventSource<DatasetStream>({
    url: getStreamUrlDatasetStream({
      filters,
      timezone,
      h3Configuration,
      datasetId
    }),
    eventKey: ["dataset-stream", datasetId, enabled],
    parse: (data) => JSON.parse(data),
    onOpen,
    eventHandlers: {
      dataset_detail: (data) => {
        datasetMetadataRef.current = data;
      },
      "dataset_data:meta": (data) => {
        datasetMetadataStreamRef.current = data;
      },
      "dataset_data:chunk": (data) => {
        chunkBufferRef.current.push(...data.rows);
      },
      "dataset_data:done": handleDatasetDone,
      "stream:done": handleStreamDone,
      "stream:error": (data) => {
        onError?.(data);
      },
      "stream:reset": (data) => {
        onError?.(data);
      },
      error: (data) => {
        onError?.(data);
      }
    },
    closeOn: ["stream:done", "stream:error", "error"],
    enabled
  });

  return {
    datasetMetadata: datasetMetadataRef.current,
    rawDataset: datasetDataRef.current?.rawDataset,
    dataset: datasetDataRef.current?.dataset
  };
}
